import type { PermissionType } from '@thinkalpha/common/contracts/permissions.js';
import { doesAccessGrantAllowRequest } from '@thinkalpha/common/util/permissions.js';
import type { FirmWithParentsAndChildren } from '@thinkalpha/ui-types/types/admin/firms';
import type { ViewerContext, ViewerMembership } from 'src/admin/firms/FirmOverview/source';
import type { RestrictedReason } from 'src/admin/lib/visible';

export interface ViewerStandingInput {
    /** Superuser privileges are on, which is what the `x-superuser` header on requests reflects. */
    isSuperuser: boolean;
    /** The account has superuser capability but privileges are off. */
    canElevate: boolean;
    /** `accessList` of `GET /users/me/security-package`; `firm` entries are the viewer's firm rungs. */
    accessList: ReadonlyArray<{ type: string; id: string; permission: PermissionType }>;
    /** Firms the viewer belongs to, from `GET /firms?userId=`, including member-only memberships. */
    memberFirms: readonly FirmWithParentsAndChildren[];
}

/**
 * Who the backends take the viewer to be: the applied perspective plus what the security package
 * and memberships say about each firm. Answers the questions the admin pages ask repeatedly, such
 * as whether a rung is held on a firm and why a refusal happened.
 */
export class ViewerStanding {
    readonly context: ViewerContext;
    readonly memberFirms: readonly FirmWithParentsAndChildren[];
    readonly #rungs = new Map<string, PermissionType>();
    readonly #memberFirmIds: ReadonlySet<string>;

    constructor(input: ViewerStandingInput) {
        for (const access of input.accessList) {
            if (access.type === 'firm') this.#rungs.set(access.id, access.permission);
        }
        this.memberFirms = input.memberFirms;
        this.#memberFirmIds = new Set(input.memberFirms.map((firm) => firm.id));
        this.context = input.isSuperuser
            ? { isSuperuser: true, canElevate: true, memberships: [] }
            : { isSuperuser: false, canElevate: input.canElevate, memberships: this.#memberships() };
    }

    #memberships(): ViewerMembership[] {
        const firmIds = new Set([...this.#memberFirmIds, ...this.#rungs.keys()]);
        // The security package carries no dependency between memberships, so none is reported here.
        return [...firmIds].map((firmId) => ({
            firmId,
            permission: this.#rungs.get(firmId) ?? null,
            active: true,
        }));
    }

    get isSuperuser(): boolean {
        return this.context.isSuperuser;
    }

    get canElevate(): boolean {
        return this.context.canElevate;
    }

    /** The viewer holds at least `rung` on the firm through their own membership, or is superuser. */
    hasRung(firmId: string, rung: PermissionType): boolean {
        if (this.isSuperuser) return true;
        const held = this.#rungs.get(firmId);
        return held !== undefined && doesAccessGrantAllowRequest(held, rung);
    }

    belongsTo(firmId: string): boolean {
        return this.#memberFirmIds.has(firmId) || this.#rungs.has(firmId);
    }

    /** Why a refusal on this firm happened: below the needed rung on a firm the viewer is in, or no standing at all. */
    refusalReason(firmId: string): Extract<RestrictedReason, 'insufficient-level' | 'other-firm'> {
        return this.belongsTo(firmId) ? 'insufficient-level' : 'other-firm';
    }

    /** Firms the viewer administers at owner or above, with the names the membership query returned. */
    ownedFirms(): FirmWithParentsAndChildren[] {
        return this.memberFirms.filter((firm) => this.hasRung(firm.id, 'owner'));
    }
}
